import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Users, TrendingUp, CheckCircle, UserPlus } from 'lucide-react';
import { organizationsService } from '../services/organizations';
import { Organization, OrganizationWithStats, DashboardStats } from '../types';
import { supabase } from '../config/supabase';
import { PeriodSelector, PeriodType, DateRange, getDateRangeForPeriod } from '../components/Dashboard/PeriodSelector';

// Extended stats interface with period-based counts
interface ExtendedDashboardStats extends DashboardStats {
  newLearnersCount: number;
  completedInPeriodCount: number;
}

export const DashboardAdmin: React.FC = () => {
  const [stats, setStats] = useState<ExtendedDashboardStats | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationWithStats[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('30days');
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRangeForPeriod('30days'));
  const [periodLabel, setPeriodLabel] = useState<string>('30 derniers jours');

  const handlePeriodChange = useCallback((period: PeriodType, range: DateRange) => {
    setSelectedPeriod(period);
    setDateRange(range);
    const labels: Record<PeriodType, string> = {
      '7days': '7 derniers jours',
      '30days': '30 derniers jours',
      '1year': '1 an',
      'custom': 'Periode selectionnee'
    };
    setPeriodLabel(labels[period]);
  }, []);

  // Load organizations list on mount
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getAll();
        setAllOrganizations(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };
    loadOrganizations();
  }, []);

  useEffect(() => {
    loadDashboardData(dateRange, selectedOrgId);
  }, [dateRange, selectedOrgId]);

  const loadDashboardData = async (range: DateRange, orgId: string) => {
    try {
      setLoading(true);

      // Load organizations with stats (filtered if org selected)
      const orgsWithStats = await organizationsService.getAllWithStats();
      if (orgId) {
        setOrganizations(orgsWithStats.filter(o => o.id === orgId));
      } else {
        setOrganizations(orgsWithStats);
      }

      // Build learner query (filtered by org if selected)
      let learnersQuery = supabase
        .from('learners')
        .select('status, created_at, organization_id');

      if (orgId) {
        learnersQuery = learnersQuery.eq('organization_id', orgId);
      }

      const { data: allLearners } = await learnersQuery;

      const { data: activeOrgs } = await supabase
        .from('organizations')
        .select('id')
        .eq('status', 'active');

      // Build enrollments query (filtered by org if selected)
      let enrollmentsQuery = supabase
        .from('enrollments')
        .select('id, completed_at, learner_id')
        .eq('status', 'completed')
        .gte('completed_at', range.startDate.toISOString())
        .lte('completed_at', range.endDate.toISOString());

      // If org is selected, we need to filter enrollments by learners in that org
      let completedEnrollments: { id: string; completed_at: string }[] | null = null;
      if (orgId) {
        // Get learner IDs for this org
        const { data: orgLearners } = await supabase
          .from('learners')
          .select('id')
          .eq('organization_id', orgId);

        if (orgLearners && orgLearners.length > 0) {
          const { data } = await supabase
            .from('enrollments')
            .select('id, completed_at')
            .eq('status', 'completed')
            .gte('completed_at', range.startDate.toISOString())
            .lte('completed_at', range.endDate.toISOString())
            .in('learner_id', orgLearners.map(l => l.id));
          completedEnrollments = data;
        } else {
          completedEnrollments = [];
        }
      } else {
        const { data } = await enrollmentsQuery;
        completedEnrollments = data;
      }

      const learners = allLearners || [];
      const totalLearners = learners.length;
      const activeLearners = learners.filter(l => l.status === 'active').length;
      const completedLearners = learners.filter(l => l.status === 'completed').length;

      // Use timestamps for reliable date comparison
      const startTime = range.startDate.getTime();
      const endTime = range.endDate.getTime();

      // Count new learners in the period
      const newLearnersInPeriod = learners.filter(l => {
        if (!l.created_at) return false;
        const createdTime = new Date(l.created_at).getTime();
        return createdTime >= startTime && createdTime <= endTime;
      }).length;

      setStats({
        organizationsCount: orgId ? 1 : orgsWithStats.length,
        activeOrganizationsCount: orgId ? (orgsWithStats.find(o => o.id === orgId)?.status === 'active' ? 1 : 0) : (activeOrgs?.length || 0),
        learnersCount: totalLearners,
        activeLearnersCount: activeLearners,
        completedLearnersCount: completedLearners,
        overallCompletionRate: totalLearners > 0
          ? Math.round((completedLearners / totalLearners) * 100)
          : 0,
        newLearnersCount: newLearnersInPeriod,
        completedInPeriodCount: completedEnrollments?.length || 0
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Tableau de bord
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {selectedOrgId
              ? `Stats pour ${allOrganizations.find(o => o.id === selectedOrgId)?.name || 'Organisation'}`
              : 'Vue d\'ensemble de Digital Learning Manager'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-400" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              <option value="">Toutes les organisations</option>
              {allOrganizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <PeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} currentRange={dateRange} />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Apprenants"
          value={stats?.learnersCount || 0}
          subtitle={`${stats?.activeLearnersCount || 0} en formation`}
          color="blue"
        />
        <StatCard
          icon={UserPlus}
          label="Nouveaux apprenants"
          value={stats?.newLearnersCount || 0}
          subtitle={periodLabel}
          color="green"
        />
        <StatCard
          icon={CheckCircle}
          label="Formations terminees"
          value={stats?.completedInPeriodCount || 0}
          subtitle={periodLabel}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          label="Taux de completion"
          value={`${stats?.overallCompletionRate || 0}%`}
          subtitle="global"
          color="purple"
        />
      </div>

      {/* Organizations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedOrgId ? 'Organisation sélectionnée' : 'Organisations'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Apprenants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Complétion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Taux
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {organizations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    Aucune organisation pour le moment
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {org.logoUrl ? (
                          <img
                            src={org.logoUrl}
                            alt={org.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {org.name}
                          </div>
                          {org.contactEmail && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {org.contactEmail}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">
                      {org.learnersCount}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-white">
                      {org.completedCount}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full max-w-[100px]">
                          <div
                            className="h-2 bg-primary rounded-full"
                            style={{ width: `${org.completionRate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {org.completionRate}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string | number;
  subtitle: string;
  color: 'blue' | 'green' | 'emerald' | 'purple' | 'orange' | 'red';
}

const colorClasses = {
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
};

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, subtitle, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>
      </div>
    </div>
  </div>
);

// Status Badge Component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    prospect: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
  };

  const labels = {
    active: 'Actif',
    inactive: 'Inactif',
    prospect: 'Prospect'
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || styles.inactive}`}>
      {labels[status as keyof typeof labels] || status}
    </span>
  );
};
